// Global Favoriten page — every favorite the user has, across projects,
// project-tasks, personal tasks and task lists, in one place. Server resolves
// names + links (access-filtered) via GET /api/me/favorites/detailed.

import { useEffect, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { Folder, ClipboardList, ListTodo, ListChecks, Star } from "lucide-preact";
import type { FavoriteEntityType } from "@juuplan/shared";
import { Layout } from "../components/Layout";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { PageShell } from "../components/ui/PageShell";
import { EmptyState } from "../components/ui/EmptyState";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { toastError } from "../lib/toast";
import {
  fetchFavoritesDetailed,
  toggleFavorite,
  type FavoriteDetail,
} from "../store/favorites";

const GROUPS: { type: FavoriteEntityType; label: string; icon: typeof Folder }[] = [
  { type: "project", label: "Projekte", icon: Folder },
  { type: "task", label: "Projekt-Aufgaben", icon: ClipboardList },
  { type: "personal_task", label: "Aufgaben", icon: ListTodo },
  { type: "list", label: "Aufgabenlisten", icon: ListChecks },
];

export function Favorites(): preact.JSX.Element {
  const [items, setItems] = useState<FavoriteDetail[] | null>(null);

  usePageHeader({
    title: requireLabel("/favorites"),
    subtitle: "Alle markierten Projekte, Aufgaben und Aufgabenlisten an einem Ort.",
  });

  useEffect(() => {
    let cancelled = false;
    void fetchFavoritesDetailed()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        // War bis hierher ein verschluckter Fehler (kein Toast, kein Inline-Text) —
        // der Nutzer sah nur eine leere Liste und konnte den Ausfall nie melden.
        toastError("Favoriten konnten nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRemove(fav: FavoriteDetail): Promise<void> {
    // Optimistic removal from the page; the store keeps the source of truth.
    setItems((prev) =>
      (prev ?? []).filter(
        (f) => !(f.entity_type === fav.entity_type && f.entity_id === fav.entity_id)
      )
    );
    try {
      await toggleFavorite(fav.entity_type, fav.entity_id);
    } catch {
      // Die Meldung kommt aus `toggleFavorite()` (store/favorites.ts) — genau
      // eine Stelle formuliert sie, hier waere sie die zweite. Diese Seite
      // laedt nur die verbindliche Liste neu, damit der Eintrag nicht
      // unerklaert wieder auftaucht.
      const data = await fetchFavoritesDetailed().catch(() => null);
      if (data) setItems(data);
    }
  }

  return (
    <Layout>
      <PageShell width="narrow">
        {items === null ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Star}
            title="Noch keine Favoriten"
            description="Markiere Projekte, Aufgaben oder Aufgabenlisten mit dem Stern, um sie hier zu sammeln."
          />
        ) : (
          <div class="flex flex-col gap-6">
            {GROUPS.map((g) => {
              const groupItems = items.filter((f) => f.entity_type === g.type);
              if (groupItems.length === 0) return null;
              const Icon = g.icon;
              return (
                <section key={g.type}>
                  <h2 class="mb-2 flex items-center gap-1.5 text-micro font-semibold uppercase tracking-widest text-[color:var(--color-on-surface-variant)]">
                    <Icon size={14} aria-hidden="true" />
                    {g.label}
                    <span class="tabular-nums opacity-70">{groupItems.length}</span>
                  </h2>
                  <ul class="flex flex-col gap-1">
                    {groupItems.map((f) => (
                      <li
                        key={`${f.entity_type}:${f.entity_id}`}
                        class="group flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 hover:bg-[color:var(--color-surface-container-low)]"
                      >
                        <span
                          class="size-2.5 shrink-0 rounded-full"
                          style={{ background: f.color || "var(--color-outline-variant)" }}
                          aria-hidden="true"
                        />
                        <Link
                          href={f.link}
                          class="flex-1 min-w-0 flex items-baseline gap-2 text-sm text-[color:var(--color-on-surface)] hover:underline"
                        >
                          <span class="truncate font-medium">{f.title}</span>
                          {f.subtitle && (
                            <span class="truncate text-xs text-[color:var(--color-on-surface-variant)]">
                              {f.subtitle}
                            </span>
                          )}
                        </Link>
                        <button
                          type="button"
                          onClick={() => void handleRemove(f)}
                          class="shrink-0 rounded p-1 text-[color:var(--color-warning,#F59E0B)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                          aria-label="Favorit entfernen"
                          title="Favorit entfernen"
                        >
                          <Star size={16} aria-hidden="true" fill="currentColor" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </PageShell>
    </Layout>
  );
}
